import { useState, useEffect, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useEnhancedGameSession } from '../hooks/useEnhancedGameSession'
import { GameHeader } from '../components/GameHeader'
import { sounds } from '../utils/sounds'
import { UserProfileManager } from '../utils/userProfile'
import '../styles/GamePage.css'
import '../styles/Werewolf.css'
import '../styles/UniversalGameRoom.css'
import '../styles/mobile-fixes.css'
import '../styles/mobile-bottom-sheet.css'
import { GameRoomSidebar } from '../components/GameRoom'
import type { Message, HistoryItem } from '../components/GameRoom/GameRoomSidebar'
import { useSidebar } from '../hooks/useSidebar'

type Role = 'werewolf' | 'villager' | 'seer' | 'witch' | 'hunter'
type Phase = 'setup' | 'lobby' | 'night' | 'day' | 'voting' | 'reveal' | 'finished'

interface Player {
  id: number
  name: string
  role: Role
  alive: boolean
}

interface GameState {
  phase: Phase
  round: number
  players: Player[]
  nightKill?: number
  werewolvesVoted: Record<number, number> // werewolf playerId -> target
  seerChecked?: { targetId: number; isWerewolf: boolean }
  witchSaved?: boolean
  witchPoisoned?: number
  hasUsedSavePotion: boolean
  hasUsedPoisonPotion: boolean
  votes: Record<number, number> // playerId -> votedForId
  lastEliminated?: { id: number; name: string; role: Role }
  hunterShot?: number
  winner?: 'werewolf' | 'villager'
  revealedInfo?: string // For showing seer results, deaths, etc.
}

const ROLE_INFO = {
  werewolf: { name: '狼人 Werewolf', desc: 'Kill villagers at night. Vote together on who to eliminate.' },
  villager: { name: '村民 Villager', desc: 'Vote out werewolves during the day. Use logic to find them!' },
  seer: { name: '预言家 Seer', desc: 'Check one player\'s role each night to find werewolves.' },
  witch: { name: '女巫 Witch', desc: 'Save one kill OR poison one player (once each per game).' },
  hunter: { name: '猎人 Hunter', desc: 'When you die, you can shoot one player before you go.' },
}

export default function Werewolf() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const autoJoinCode = searchParams.get('join')

  // Auto-fill from profile
  const userProfile = UserProfileManager.get()

  const [playerName, setPlayerName] = useState(userProfile?.name || '')
  const [playerNumber, setPlayerNumber] = useState<1 | 2>(1)
  const [setupPhase, setSetupPhase] = useState<'select' | 'create' | 'join'>('select')
  const [playerCount, setPlayerCount] = useState(6) // Minimum 6 players

  const enhancedSession = useEnhancedGameSession({
    sessionCode: undefined,
    playerNumber,
    playerName,
  })

  const { session, createSession, joinSession, voice,  sendMove, onMessage } = enhancedSession

  const [gameState, setGameState] = useState<GameState>({
    phase: 'setup',
    round: 1,
    players: [],
    werewolvesVoted: {},
    votes: {},
    hasUsedSavePotion: false,
    hasUsedPoisonPotion: false,
  })

  const [myRole, setMyRole] = useState<Role | null>(null)
  const [revealedRole, setRevealedRole] = useState(false)
  const [myVote, setMyVote] = useState<number | null>(null)

  // Universal sidebar state
  const [chatMessages, setChatMessages] = useState<Message[]>([])
  const [historyItems] = useState<HistoryItem[]>([])
  const sidebar = useSidebar()

  // Handler for sidebar message sending
  const handleSidebarMessage = useCallback(async (message: string) => {
    if (session?.session_code) {
      await sendMove({ type: 'chat', message })
      // Add to local messages immediately
      const newMessage: Message = {
        sender: 'you',
        content: message,
        timestamp: Date.now()
      }
      setChatMessages(prev => [...prev, newMessage])
    }
  }, [session, sendMove])

  // Sync game state from host
  useEffect(() => {
    const unsubscribe = onMessage('game_state', (data) => {
      console.log('[Werewolf] Received game state:', data)
      setGameState(data.state)

      // Set my role if not set
      if (!myRole && data.state.players[playerNumber - 1]) {
        setMyRole(data.state.players[playerNumber - 1].role)
      }
    })

    return () => unsubscribe()
  }, [onMessage, playerNumber, myRole])

  // Handle chat messages
  useEffect(() => {
    const unsubscribe = onMessage('chat', (data) => {
      console.log('[Werewolf] Chat message:', data.message)
      const newMessage: Message = {
        sender: data.playerNumber === playerNumber ? 'you' : 'opponent',
        content: data.message,
        timestamp: Date.now()
      }
      setChatMessages(prev => [...prev, newMessage])
    })

    return () => unsubscribe()
  }, [onMessage, playerNumber])

  // Track player count
  useEffect(() => {
    if (session?.player1_name && session?.player2_name) {
      // For now, we'll use player2_name as a proxy for "at least 2 players joined"
      // In reality, we need to track all players in the session
      setPlayerCount(Math.max(6, playerCount))
    }
  }, [session, playerCount])

  // Initialize game with roles
  const startGame = () => {
    if (playerNumber !== 1) {
      alert('Only the host can start the game!')
      return
    }

    if (playerCount < 6) {
      alert('Need at least 6 players to start!')
      return
    }

    // Role distribution for 6-8 players (simplified)
    const roles: Role[] = []
    if (playerCount === 6) {
      roles.push('werewolf', 'werewolf', 'seer', 'witch', 'villager', 'villager')
    } else if (playerCount === 7) {
      roles.push('werewolf', 'werewolf', 'seer', 'witch', 'hunter', 'villager', 'villager')
    } else {
      roles.push('werewolf', 'werewolf', 'werewolf', 'seer', 'witch', 'hunter', 'villager', 'villager')
    }

    // Shuffle roles
    const shuffled = roles.sort(() => Math.random() - 0.5)

    const players: Player[] = []
    for (let i = 1; i <= playerCount; i++) {
      players.push({
        id: i,
        name: i === 1 ? session?.player1_name || 'Player 1' :
              i === 2 ? session?.player2_name || 'Player 2' :
              `Player ${i}`,
        role: shuffled[i - 1],
        alive: true,
      })
    }

    const newState: GameState = {
      phase: 'night',
      round: 1,
      players,
      werewolvesVoted: {},
      votes: {},
      hasUsedSavePotion: false,
      hasUsedPoisonPotion: false,
      revealedInfo: 'Night falls. Werewolves, choose your victim...',
    }

    setGameState(newState)
    setMyRole(players[0].role) // Set host's role
    sendMove({ type: 'game_state', state: newState })
    sounds.success()
  }

  // Werewolf vote to kill
  const werewolfVote = (targetId: number) => {
    if (myRole !== 'werewolf') return

    const newWerewolvesVoted = { ...gameState.werewolvesVoted }
    newWerewolvesVoted[playerNumber] = targetId

    const newState = { ...gameState, werewolvesVoted: newWerewolvesVoted }
    setGameState(newState)
    sendMove({ type: 'game_state', state: newState })
    sounds.click()
    setMyVote(targetId)
  }

  // Seer check role
  const seerCheck = (targetId: number) => {
    if (myRole !== 'seer') return
    if (gameState.seerChecked) return // Already checked this night

    const target = gameState.players.find(p => p.id === targetId)
    if (!target) return

    const isWerewolf = target.role === 'werewolf'
    const newState = {
      ...gameState,
      seerChecked: { targetId, isWerewolf },
      revealedInfo: `You checked ${target.name}. They are ${isWerewolf ? 'a WEREWOLF' : 'NOT a werewolf'}.`,
    }

    setGameState(newState)
    sendMove({ type: 'game_state', state: newState })
    sounds.click()
  }

  // Witch save
  const witchSave = () => {
    if (myRole !== 'witch') return
    if (gameState.hasUsedSavePotion) return
    if (!gameState.nightKill) return

    const newState = {
      ...gameState,
      witchSaved: true,
      hasUsedSavePotion: true,
      revealedInfo: 'You saved the victim!',
    }

    setGameState(newState)
    sendMove({ type: 'game_state', state: newState })
    sounds.click()
  }

  // Witch poison
  const witchPoison = (targetId: number) => {
    if (myRole !== 'witch') return
    if (gameState.hasUsedPoisonPotion) return
    if (gameState.witchPoisoned) return

    const newState = {
      ...gameState,
      witchPoisoned: targetId,
      hasUsedPoisonPotion: true,
      revealedInfo: 'You poisoned someone!',
    }

    setGameState(newState)
    sendMove({ type: 'game_state', state: newState })
    sounds.click()
  }

  // End night phase (host only)
  const endNight = () => {
    if (playerNumber !== 1) return

    // Determine who werewolves killed (majority vote)
    const werewolfVotes: Record<number, number> = {}
    Object.values(gameState.werewolvesVoted).forEach(targetId => {
      werewolfVotes[targetId] = (werewolfVotes[targetId] || 0) + 1
    })

    let maxVotes = 0
    let killedId = 0
    Object.entries(werewolfVotes).forEach(([id, count]) => {
      if (count > maxVotes) {
        maxVotes = count
        killedId = Number(id)
      }
    })

    const newState = { ...gameState, nightKill: killedId }

    // Process deaths
    const deaths: string[] = []

    // Werewolf kill (unless saved by witch)
    if (killedId && !newState.witchSaved) {
      const player = newState.players.find(p => p.id === killedId)
      if (player) {
        player.alive = false
        deaths.push(`${player.name} was killed by werewolves`)
      }
    } else if (killedId && newState.witchSaved) {
      deaths.push('Someone was saved by the witch!')
    }

    // Witch poison
    if (newState.witchPoisoned) {
      const player = newState.players.find(p => p.id === newState.witchPoisoned)
      if (player) {
        player.alive = false
        deaths.push(`${player.name} was poisoned`)
      }
    }

    newState.phase = 'day'
    newState.revealedInfo = deaths.length > 0 ? deaths.join('. ') : 'Nobody died last night!'
    newState.werewolvesVoted = {}
    newState.seerChecked = undefined
    newState.witchSaved = undefined
    newState.witchPoisoned = undefined

    checkWinCondition(newState)

    setGameState(newState)
    sendMove({ type: 'game_state', state: newState })
    setMyVote(null)
    sounds.turnChange()
  }

  // Cast vote during day
  const castVote = (targetId: number) => {
    const newVotes = { ...gameState.votes }
    newVotes[playerNumber] = targetId

    const newState = { ...gameState, votes: newVotes }
    setGameState(newState)
    sendMove({ type: 'game_state', state: newState })
    sounds.click()
    setMyVote(targetId)
  }

  // End voting (host only)
  const endVoting = () => {
    if (playerNumber !== 1) return

    const voteCounts: Record<number, number> = {}
    Object.values(gameState.votes).forEach(targetId => {
      voteCounts[targetId] = (voteCounts[targetId] || 0) + 1
    })

    let maxVotes = 0
    let eliminatedId = 0
    Object.entries(voteCounts).forEach(([id, count]) => {
      if (count > maxVotes) {
        maxVotes = count
        eliminatedId = Number(id)
      }
    })

    let revealedInfo = ''
    let lastEliminated = undefined

    if (eliminatedId) {
      const eliminated = gameState.players.find(p => p.id === eliminatedId)
      if (eliminated) {
        eliminated.alive = false
        lastEliminated = { id: eliminated.id, name: eliminated.name, role: eliminated.role }
        revealedInfo = `${eliminated.name} was eliminated. They were a ${ROLE_INFO[eliminated.role].name}!`
      }
    } else {
      revealedInfo = 'No one was eliminated (tie vote).'
    }

    const newState: GameState = {
      ...gameState,
      phase: 'reveal',
      votes: {},
      revealedInfo,
      lastEliminated,
    }

    checkWinCondition(newState)

    setGameState(newState)
    sendMove({ type: 'game_state', state: newState })
    setMyVote(null)
    sounds.turnChange()

    // Auto-advance after 5 seconds
    setTimeout(() => {
      if (newState.winner) return

      // Check if hunter was killed
      if (lastEliminated?.role === 'hunter') {
        newState.phase = 'day'
        newState.revealedInfo = 'Hunter, choose who to shoot!'
        setGameState(newState)
        sendMove({ type: 'game_state', state: newState })
      } else {
        startNewNight(newState)
      }
    }, 5000)
  }

  // Hunter shoots
  const hunterShoot = (targetId: number) => {
    if (myRole !== 'hunter') return
    if (gameState.hunterShot) return

    const target = gameState.players.find(p => p.id === targetId)
    if (!target) return

    target.alive = false

    const newState = {
      ...gameState,
      hunterShot: targetId,
      revealedInfo: `Hunter shot ${target.name}! They were a ${ROLE_INFO[target.role].name}.`,
    }

    checkWinCondition(newState)

    setGameState(newState)
    sendMove({ type: 'game_state', state: newState })
    sounds.click()

    // Start new night after 3 seconds
    setTimeout(() => {
      if (!newState.winner) {
        startNewNight(newState)
      }
    }, 3000)
  }

  const startNewNight = (state: GameState) => {
    if (playerNumber !== 1) return

    const newState: GameState = {
      ...state,
      phase: 'night',
      round: state.round + 1,
      werewolvesVoted: {},
      nightKill: undefined,
      revealedInfo: `Night ${state.round + 1}. Werewolves, choose your victim...`,
      hunterShot: undefined,
      lastEliminated: undefined,
    }

    setGameState(newState)
    sendMove({ type: 'game_state', state: newState })
  }

  const checkWinCondition = (state: GameState) => {
    const alivePlayers = state.players.filter(p => p.alive)
    const aliveWerewolves = alivePlayers.filter(p => p.role === 'werewolf')
    const aliveVillagers = alivePlayers.filter(p => p.role !== 'werewolf')

    if (aliveWerewolves.length === 0) {
      state.winner = 'villager'
      state.phase = 'finished'
      sounds.success()
    } else if (aliveWerewolves.length >= aliveVillagers.length) {
      state.winner = 'werewolf'
      state.phase = 'finished'
      sounds.success()
    }
  }

  // Auto-join
  useEffect(() => {
    if (autoJoinCode && setupPhase === 'select') {
      setSetupPhase('join')
    }
  }, [autoJoinCode, setupPhase])

  // Setup screens
  if (setupPhase === 'select') {
    return (
      <div className="werewolf-container">
        <button onClick={() => navigate('/')} className="back-button">← Back to Games</button>
        <div className="game-setup">
          <h1>🐺 狼人杀 Werewolf</h1>
          <p>A social deduction game for 6-8 players</p>
          <div className="setup-actions">
            <button onClick={() => setSetupPhase('create')} className="btn btn-primary">🎮 Create Game Room</button>
            <button onClick={() => setSetupPhase('join')} className="btn btn-secondary">Join Game</button>
          </div>
        </div>
      </div>
    )
  }

  if (setupPhase === 'create') {
    return (
      <div className="werewolf-container">
        <button onClick={() => setSetupPhase('select')} className="back-button">← Back</button>
        <div className="lobby-container">
          <h1>🐺 狼人杀 Werewolf</h1>
          <p className="lobby-subtitle">Create a new game room</p>

          <div className="setup-section">
            <h3>Your Name</h3>
            <input
              type="text"
              placeholder="Enter your name"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              maxLength={20}
              className="player-name-input"
            />
          </div>

          <div className="setup-section">
            <h3>Number of Players</h3>
            <select
              value={playerCount}
              onChange={(e) => setPlayerCount(Number(e.target.value))}
              className="player-name-input"
            >
              <option value={6}>6 Players</option>
              <option value={7}>7 Players</option>
              <option value={8}>8 Players</option>
            </select>
          </div>

          <button
            onClick={async () => {
              if (!playerName) return
              await createSession('werewolf', playerName)
              setPlayerNumber(1)
              setGameState({ ...gameState, phase: 'lobby' })
            }}
            disabled={!playerName}
            className="start-button"
          >
            Create Game Room
          </button>
        </div>
      </div>
    )
  }

  if (setupPhase === 'join') {
    return (
      <div className="werewolf-container">
        <button onClick={() => setSetupPhase('select')} className="back-button">← Back</button>
        <div className="lobby-container">
          <h1>🐺 狼人杀 Werewolf</h1>
          <p className="lobby-subtitle">Join an existing game</p>

          <div className="setup-section">
            <h3>Your Name</h3>
            <input
              type="text"
              placeholder="Enter your name"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              maxLength={20}
              className="player-name-input"
            />
          </div>

          <div className="setup-section">
            <h3>Game Code</h3>
            <input
              type="text"
              placeholder="Enter game code"
              defaultValue={autoJoinCode || ''}
              id="join-code"
              className="player-name-input"
            />
          </div>

          <button
            onClick={async () => {
              const code = (document.getElementById('join-code') as HTMLInputElement).value
              if (!playerName || !code) return
              await joinSession(code, playerName)
              setPlayerNumber(2)
              setGameState({ ...gameState, phase: 'lobby' })
            }}
            disabled={!playerName}
            className="start-button"
          >
            Join Game
          </button>
        </div>
      </div>
    )
  }

  // Lobby
  if (gameState.phase === 'lobby') {
    return (
      <div className="werewolf-container">
        <GameHeader
          gameTitle="🐺 Werewolf"
          players={[
            { number: 1, name: session?.player1_name || 'Player 1' },
            { number: 2, name: session?.player2_name || 'Player 2' },
          ].filter((_, i) => i < playerCount)}
          sessionCode={session?.session_code}
          voiceControls={{
            connectionStatus: voice.connectionStatus,
            voiceStatus: voice.voiceStatus,
            onToggleMute: voice.toggleMute,
            onConnect: voice.connect,
            onDisconnect: voice.disconnect,
          }}
        />

        <div className="lobby-container">
          <h1>🐺 狼人杀 Werewolf</h1>
          <h2>Waiting for players...</h2>

          <div className="session-info">
            <p><strong>Session Code:</strong> <code>{session?.session_code}</code></p>
            <p><strong>Players Needed:</strong> {playerCount}</p>
            <p><strong>Join URL:</strong></p>
            <code>{window.location.origin}/werewolf?join={session?.session_code}</code>
            <button
              onClick={() => {
                const url = `${window.location.origin}/werewolf?join=${session?.session_code}`
                navigator.clipboard.writeText(url)
                sounds.click()
              }}
              className="btn btn-primary"
              style={{ marginTop: 'var(--space-md)' }}
            >
              📋 Copy URL
            </button>
          </div>

          <div className="player-requirements">
            <p>⚠️ This game requires exactly {playerCount} players</p>
            <p>Make sure all players join before starting!</p>
          </div>

          {playerNumber === 1 && (
            <button className="start-button" onClick={startGame}>
              Start Game ({playerCount} Players)
            </button>
          )}

          {playerNumber !== 1 && (
            <p className="waiting-text">Waiting for host to start...</p>
          )}
        </div>
      </div>
    )
  }

  // Main game
  const me = gameState.players[playerNumber - 1]
  const isWerewolf = myRole === 'werewolf'
  const isSeer = myRole === 'seer'
  const isWitch = myRole === 'witch'
  const isHunter = myRole === 'hunter'
  const isHost = playerNumber === 1

  return (
    <div className="werewolf-container universal-game-room">
      <GameHeader
        gameTitle="🐺 Werewolf"
        players={gameState.players.map(p => ({
          number: p.id,
          name: p.name,
          isConnected: p.alive,
        }))}
        sessionCode={session?.session_code}
        voiceControls={{
          connectionStatus: voice.connectionStatus,
          voiceStatus: voice.voiceStatus,
          onToggleMute: voice.toggleMute,
          onConnect: voice.connect,
          onDisconnect: voice.disconnect,
        }}
      />

      <div className="game-info" style={{ marginTop: '60px', padding: 'var(--space-md)', textAlign: 'center' }}>
        <span>Round {gameState.round}</span>
        <span className="phase-badge" style={{ marginLeft: '1rem' }}>{gameState.phase}</span>
        {voice.isConnected && <span className="webrtc-badge" style={{ marginLeft: '1rem' }}>⚡ Real-time</span>}
      </div>

      {gameState.phase === 'finished' && (
        <div className="winner-overlay">
          <h2>
            {gameState.winner === 'werewolf' ? '🐺 Werewolves Win!' : '👥 Villagers Win!'}
          </h2>
          <p>
            {isWerewolf && gameState.winner === 'werewolf' && 'Your team won!'}
            {!isWerewolf && gameState.winner === 'villager' && 'Your team won!'}
            {isWerewolf && gameState.winner === 'villager' && 'Your team lost.'}
            {!isWerewolf && gameState.winner === 'werewolf' && 'Your team lost.'}
          </p>
          <div className="final-roles">
            <h3>Roles Revealed:</h3>
            {gameState.players.map(p => (
              <div key={p.id}>
                {p.name}: {ROLE_INFO[p.role].name}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="game-room-content">
        <div className="game-board game-board-area">
        {/* Role card */}
        <div className="role-card" onClick={() => setRevealedRole(!revealedRole)}>
          <h3>Your Role</h3>
          {revealedRole && myRole ? (
            <>
              <div className="role-name">{ROLE_INFO[myRole].name}</div>
              <div className="role-desc">{ROLE_INFO[myRole].desc}</div>
            </>
          ) : (
            <div className="role-hidden">Click to reveal</div>
          )}
        </div>

        {/* Info display */}
        {gameState.revealedInfo && (
          <div className="info-banner">
            <p>{gameState.revealedInfo}</p>
          </div>
        )}

        {/* Players grid */}
        <div className="players-grid">
          {gameState.players.map(player => (
            <div
              key={player.id}
              className={`player-card ${!player.alive ? 'dead' : ''} ${
                player.id === playerNumber ? 'me' : ''
              }`}
            >
              <div className="player-name">{player.name}</div>
              <div className="player-status">
                {!player.alive && '💀 Dead'}
                {player.alive && player.id === playerNumber && '(You)'}
                {myVote === player.id && '✓ Your Vote'}
              </div>

              {/* Night phase actions */}
              {gameState.phase === 'night' && me?.alive && (
                <>
                  {isWerewolf && player.alive && player.id !== playerNumber && !player.role.includes('werewolf') && (
                    <button
                      onClick={() => werewolfVote(player.id)}
                      disabled={!!gameState.werewolvesVoted[playerNumber]}
                    >
                      {gameState.werewolvesVoted[playerNumber] === player.id ? '✓ Voted' : 'Kill'}
                    </button>
                  )}

                  {isSeer && player.alive && player.id !== playerNumber && !gameState.seerChecked && (
                    <button onClick={() => seerCheck(player.id)}>
                      Check Role
                    </button>
                  )}

                  {isWitch && player.alive && player.id !== playerNumber &&
                    !gameState.hasUsedPoisonPotion && !gameState.witchPoisoned && (
                    <button onClick={() => witchPoison(player.id)}>
                      Poison
                    </button>
                  )}
                </>
              )}

              {/* Voting phase */}
              {gameState.phase === 'voting' && player.alive && player.id !== playerNumber && me?.alive && (
                <button onClick={() => castVote(player.id)}>
                  {gameState.votes[playerNumber] === player.id ? '✓ Voted' : 'Vote Out'}
                </button>
              )}

              {/* Hunter shoot */}
              {gameState.phase === 'day' && gameState.lastEliminated?.role === 'hunter' &&
                isHunter && !gameState.hunterShot && player.alive && (
                <button onClick={() => hunterShoot(player.id)}>
                  Shoot
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Phase controls */}
        {gameState.phase === 'night' && (
          <div className="phase-info">
            <h3>🌙 Night Phase</h3>
            {isWerewolf && <p>Vote with your fellow werewolves on who to kill</p>}
            {isSeer && !gameState.seerChecked && <p>Check one player's role</p>}
            {isWitch && (
              <div>
                <p>Witch abilities:</p>
                {gameState.nightKill && !gameState.hasUsedSavePotion && (
                  <button onClick={witchSave}>Save Tonight's Victim</button>
                )}
                {!gameState.hasUsedPoisonPotion && <p>Or poison someone (click their card)</p>}
              </div>
            )}
            {!isWerewolf && !isSeer && !isWitch && <p>Sleep tight...</p>}

            {isHost && (
              <button onClick={endNight} className="host-button">
                End Night Phase
              </button>
            )}
          </div>
        )}

        {gameState.phase === 'day' && !gameState.lastEliminated && (
          <div className="phase-info">
            <h3>☀️ Day Phase</h3>
            <p>Discuss who you think is a werewolf, then vote!</p>
            {isHost && (
              <button onClick={() => setGameState({ ...gameState, phase: 'voting' })}>
                Start Voting
              </button>
            )}
          </div>
        )}

        {gameState.phase === 'voting' && (
          <div className="phase-info">
            <h3>🗳️ Voting Phase</h3>
            <p>Vote for who you want to eliminate</p>
            <p>Votes cast: {Object.keys(gameState.votes).length} / {gameState.players.filter(p => p.alive).length}</p>
            {isHost && (
              <button onClick={endVoting}>Finish Voting</button>
            )}
          </div>
        )}

        <div className="game-rules">
          <details>
            <summary>📖 How to Play</summary>
            <ul>
              <li><strong>Werewolves:</strong> Vote together at night to kill a villager</li>
              <li><strong>Villagers:</strong> Vote during the day to eliminate werewolves</li>
              <li><strong>Seer:</strong> Check one player's role each night</li>
              <li><strong>Witch:</strong> Can save the kill target OR poison someone (once each)</li>
              <li><strong>Hunter:</strong> When eliminated, shoot one player before dying</li>
              <li><strong>Win:</strong> Villagers win if all werewolves are eliminated</li>
              <li><strong>Win:</strong> Werewolves win if they equal or outnumber villagers</li>
            </ul>
          </details>
        </div>
      </div>
      </div>

      {/* Universal Sidebar with bottom-sheet mode on mobile */}
      <GameRoomSidebar
        mode="network"
        gameState={{
          turn: gameState.phase === 'night' ? '🌙 Night' : gameState.phase === 'day' ? '☀️ Day' : gameState.phase === 'voting' ? '🗳️ Voting' : 'Playing',
          phase: gameState.winner ? 'Game Over' : 'Playing',
          score: {
            'Alive': `${gameState.players.filter(p => p.alive).length}`,
            'Dead': `${gameState.players.filter(p => !p.alive).length}`
          }
        }}
        messages={chatMessages}
        historyItems={historyItems}
        onSendMessage={handleSidebarMessage}
        collapsed={sidebar.collapsed}
        onToggleCollapse={sidebar.toggle}
        position={sidebar.position}
        quickReactions={true}
      />
    </div>
  )
}
